import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { get, textoDeErroGravado } from '../../nucleo/api';
import { rotuloJob, rotuloModulo } from '../../nucleo/rotulos';
import { dataHora, numero, relativo } from '../../nucleo/formato';
import { COR_ESTADO_JOB, rotuloEstado, useSaudeNucleo } from './Painel';
import type { SaudeDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

function Par({ rotulo, valor, mono = true }: { rotulo: string; valor: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-tinta-3">{rotulo}</dt>
      <dd className={`break-all ${mono ? 'numero' : ''}`}>{valor}</dd>
    </div>
  );
}

/** M12.6 Saúde: detalhe técnico com versões, fila do outbox, sinal do worker e erros com a mensagem completa. */
export function Saude() {
  const { t } = useTranslation('admin');
  const nucleo = useSaudeNucleo();
  const detalhe = useQuery({ queryKey: ['/admin/saude/detalhe'], queryFn: () => get<SaudeDetalhe>('/admin/saude/detalhe'), refetchInterval: 30_000 });
  const atualizar = () => Promise.all([nucleo.refetch(), detalhe.refetch()]);
  return (
    <>
      <AcaoPrimaria>
        <Botao icone={<RefreshCw className="size-4" />} carregando={detalhe.isFetching || nucleo.isFetching} onClick={atualizar}>
          {t('saude.atualizar')}
        </Botao>
      </AcaoPrimaria>
      <Estado consulta={detalhe}>
        {(d) => (
          <div className="flex flex-col gap-5">
            <Cartao titulo={t('saude.versoes')}>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <Par rotulo={t('saude.aplicacao')} valor={d.versoes.app} />
                <Par rotulo={t('saude.node')} valor={d.versoes.node} />
                <Par rotulo={t('saude.postgres')} valor={d.versoes.postgres} />
                <Par
                  rotulo={t('saude.worker')}
                  valor={nucleo.data?.worker.sha ?? t('saude.semSinal')}
                />
                <Par
                  rotulo={t('saude.sinal')}
                  mono={false}
                  valor={nucleo.data?.worker.ultimo_sinal ? `${dataHora(nucleo.data.worker.ultimo_sinal)} (${relativo(nucleo.data.worker.ultimo_sinal)})` : t('saude.semSinal')}
                />
              </dl>
            </Cartao>

            <Cartao titulo={t('saude.modulos')}>
              <ul className="grid gap-x-6 sm:grid-cols-2">
                {d.modulos.map((m) => (
                  <li key={m.slug} className="flex items-center justify-between gap-3 border-b border-borda py-2 text-sm last:border-0">
                    <TextoCortado className="min-w-0">
                      {rotuloModulo(m.slug, m.nome)} <span className="numero text-xs text-tinta-3">{m.versao}</span>
                    </TextoCortado>
                    <Selo cor={m.instalado ? 'verde' : 'neutro'}>{m.instalado ? t('modulos.instalado') : t('modulos.naoInstalado')}</Selo>
                  </li>
                ))}
              </ul>
            </Cartao>

            <Cartao titulo={t('saude.fila')}>
              <h3 className="kicker mb-2">{t('saude.filaAgora')}</h3>
              {d.outbox.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('saude.filaVazia')}</p>
              ) : (
                <ul className="flex flex-wrap gap-3">
                  {d.outbox.map((o) => (
                    <li key={o.estado} className="flex items-center gap-2 text-sm">
                      <Selo cor={COR_ESTADO_JOB[o.estado] ?? 'neutro'}>{rotuloEstado(t, o.estado)}</Selo>
                      <span className="numero">{numero(o.total)}</span>
                      {o.mais_antigo && <span className="text-xs text-tinta-3">{t('saude.filaMaisAntigo', { quando: relativo(o.mais_antigo) })}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {d.outbox_24h.length > 0 && (
                <>
                  <h3 className="kicker mt-4 mb-2">{t('saude.fila24h')}</h3>
                  <ul className="flex flex-wrap gap-3">
                    {d.outbox_24h.map((o) => (
                      <li key={o.estado} className="flex items-center gap-2 text-sm">
                        <Selo cor={COR_ESTADO_JOB[o.estado] ?? 'neutro'}>{rotuloEstado(t, o.estado)}</Selo>
                        <span className="numero">{numero(o.total)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Cartao>

            <Cartao titulo={t('saude.jobsPorTipo')} semPadding>
              {d.jobs_por_tipo_24h.length === 0 ? (
                <p className="p-4 text-sm text-tinta-3">{t('painel.semJobs')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className="px-4 py-2 font-medium">{t('saude.tipo')}</th>
                        <th scope="col" className="px-4 py-2 font-medium">{t('saude.estado')}</th>
                        <th scope="col" className="px-4 py-2 text-right font-medium">{t('saude.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.jobs_por_tipo_24h.map((j) => (
                        <tr key={`${j.tipo}-${j.estado}`} className="border-b border-borda last:border-0">
                          <td className="px-4 py-2" title={j.tipo}>{rotuloJob(j.tipo)}</td>
                          <td className="px-4 py-2"><Selo cor={COR_ESTADO_JOB[j.estado] ?? 'neutro'}>{rotuloEstado(t, j.estado)}</Selo></td>
                          <td className="numero px-4 py-2 text-right">{numero(j.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Cartao>

            <Cartao titulo={t('saude.erros')}>
              {d.jobs_com_erro.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('saude.semErros')}</p>
              ) : (
                <ul className="flex flex-col gap-4">
                  {d.jobs_com_erro.map((j) => (
                    <li key={j.id} className="min-w-0">
                      <p className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="font-medium" title={j.tipo}>{rotuloJob(j.tipo)}</span>
                        <span className="text-xs text-tinta-3">
                          {t('saude.tentativas', { count: j.tentativas })} · {dataHora(j.created_at)}
                        </span>
                      </p>
                      <pre className="numero mt-1 overflow-x-auto rounded-md bg-sup-2 p-2 text-xs break-words whitespace-pre-wrap text-perigo">{textoDeErroGravado(j.erro)}</pre>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>

            {d.webhooks_falhos.length > 0 && (
              <Cartao titulo={t('saude.webhooks')}>
                <ul className="flex flex-col gap-4">
                  {d.webhooks_falhos.map((w) => (
                    <li key={w.id} className="min-w-0">
                      <p className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="numero font-medium">{w.evento}</span>
                        <span className="text-xs text-tinta-3">
                          {w.status_http ? `HTTP ${w.status_http} · ` : ''}
                          {t('saude.tentativas', { count: w.tentativa })} · {dataHora(w.updated_at)}
                        </span>
                      </p>
                      <p className="truncate text-xs text-tinta-3" title={w.url}>{w.url}</p>
                      {w.resposta && <pre className="numero mt-1 overflow-x-auto rounded-md bg-sup-2 p-2 text-xs break-words whitespace-pre-wrap text-perigo">{w.resposta}</pre>}
                    </li>
                  ))}
                </ul>
              </Cartao>
            )}

            {d.eventos_recebidos_com_erro.length > 0 && (
              <Cartao titulo={t('saude.eventosErro')}>
                <ul className="flex flex-col gap-4">
                  {d.eventos_recebidos_com_erro.map((e) => (
                    <li key={e.id} className="min-w-0">
                      <p className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="numero font-medium">{e.conector}</span>
                        <span className="text-xs text-tinta-3">{dataHora(e.created_at)}</span>
                      </p>
                      <pre className="numero mt-1 overflow-x-auto rounded-md bg-sup-2 p-2 text-xs break-words whitespace-pre-wrap text-perigo">{textoDeErroGravado(e.erro)}</pre>
                    </li>
                  ))}
                </ul>
              </Cartao>
            )}
          </div>
        )}
      </Estado>
    </>
  );
}
